/**
 * Small metadata readers shared across the ADSP entity-tree levels so the CCG,
 * concept, creative, and asset cards derive display names and detail panels the
 * same way without each re-deriving publisher-metadata shapes.
 */
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import type { AdsStatusFilter } from './statusFilterParams';

/** Reads the CCG display name from PRODUCT_SET metadata, degrading to undefined. */
export function readCcgName (entity: PublisherEntityDto): string | undefined {
  const meta = entity.publisherMetadata as Record<string, unknown> | undefined;
  return typeof meta?.ccgName === 'string' ? meta.ccgName : undefined;
}

/** Reads a concept/creative display name from metadata, degrading to undefined. */
export function readMetadataName (entity: PublisherEntityDto): string | undefined {
  const meta = entity.publisherMetadata as Record<string, unknown> | undefined;
  return typeof meta?.name === 'string' ? meta.name : undefined;
}

/**
 * Builds the metadata shown in a details panel: everything except the display
 * name (already the panel's title) with empty values dropped. Mirrors how the
 * Google campaign/ad-group panels prepare their metadata.
 */
export function readDetailMetadata (entity: PublisherEntityDto): Record<string, unknown> | undefined {
  const meta = entity.publisherMetadata as Record<string, unknown> | undefined;
  if (!meta) return undefined;
  const { name: _name, ...rest } = meta;
  const filtered = Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value !== undefined && value !== null),
  );
  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

/**
 * Picks the empty-state message for a level: an active status filter yields a
 * "nothing matches this filter" message, while the unfiltered ALL view yields
 * the level's structural "no children" message.
 */
export function resolveEmptyStateKey (statusFilter: AdsStatusFilter, noChildrenKey: string): string {
  return statusFilter === 'ALL' ? noChildrenKey : 'ads_manager_no_filter_results';
}

/**
 * Buckets child rows under their parent's sourceEntityId so one batched child
 * query (e.g. every asset of a concept's creatives) can be handed out per parent.
 * Rows without a parent link are dropped: they cannot belong to any rendered parent.
 */
export function groupByParentSourceEntityId (
  entities: readonly PublisherEntityDto[],
): Map<string, PublisherEntityDto[]> {
  const groups = new Map<string, PublisherEntityDto[]>();
  for (const entity of entities) {
    const parentId = entity.parentSourceEntityId;
    if (parentId == null) continue;
    const group = groups.get(parentId);
    if (group) {
      group.push(entity);
    } else {
      groups.set(parentId, [entity]);
    }
  }
  return groups;
}
